import {
    DSAR_DISPLAY_OPTIONS,
    DSAR_FIELD_TYPES,
    DSAR_STANDARD_FIELDS_I18N,
} from '/b2b/dsarForms/constants';
import {
    getEntityById as getOrganizationById,
    getEntitiesForIds as getOrganizationsForIds,
} from '../../organizations/state/db/Organization.selectors';

import { DOMAIN } from './FormDetails.constants';
import castArray from 'lodash/castArray';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import { translate } from '../../common/helpers/i18n';

const filteredKeys = ['order'];
const mandatoryKeys = ['id', 'name', 'type', 'required'];

export const getStore = state => get(state, DOMAIN) || {};

export const getId = state => getStore(state).id;
export const getError = state => getStore(state).error;
export const getUpdateError = state => getStore(state).updateError;

export const getChanges = state => getStore(state).changes || {};
export const getSavedChanges = state => getStore(state).savedChanges;
export const hasUnsavedChanges = state => {
    const saved = getSavedChanges(state);
    if (!saved) return false;
    return !isEqual(getChanges(state), saved);
};
export const getName = state => getChanges(state).name || '';
export const getDescription = state => getChanges(state).description || '';
export const getOrganizations = state =>
    getOrganizationsForIds(
        state,
        (getChanges(state).organizations || []).map(({ orgId }) => orgId)
    ).filter(org => org && !org.isRoot);
export const getCustomFields = state => getChanges(state).customFields || [];
export const getFieldById = (state, id) => getChanges(state).fields[id] || {};
export const getFields = state =>
    getCustomFields(state).map(({ id }) => ({ ...getFieldById(state, id) }));
export const getFieldByIndex = (state, index) =>
    getFieldById(state, getCustomFields(state)[index]) || {};

export const getSavedCustomFields = state => (getSavedChanges(state) || {}).customFields || [];
export const getSavedFieldById = (state, id) =>
    ((getSavedChanges(state) || {}).fields || {})[id] || {};
// Persisted form fields used for the bulk import CSV template so the template
// matches the saved form, not unsaved edits still in `changes`.
export const getSavedFields = state =>
    getSavedCustomFields(state).map(({ id }) => ({ ...getSavedFieldById(state, id) }));

export const getField = (state, field) => {
    const value = get(getChanges(state), field);
    return value === undefined || value === null ? '' : value;
};
export const getErrors = state => getStore(state).errors || {};
export const getFieldErrors = (state, field) => get(getErrors(state), field, '');
export const getFieldOptionsById = (state, id) => get(getFieldById(state, id), 'options') || [];

export const getRequestTypes = state => getChanges(state).requestTypes || [];
export const getUnexcludedRequestTypes = state =>
    getRequestTypes(state).filter(({ jurisdiction } = {}) => jurisdiction !== 'disabled') || [];

export const getConditionalFieldsById = (state, id) =>
    getCustomFields(state).filter(
        ({ conditional: { id: conditionalId } = {} }) => conditionalId === id
    ) || [];

export const getConditionalFieldsByValue = (state, id, value) =>
    getCustomFields(state).filter(
        ({ conditional: { id: conditionalId, value: conditionalValue } = {} } = {}) =>
            conditionalId === id && conditionalValue === value
    ) || [];

export const getCustomFieldErrors = (state, id) => {
    const errors = getErrors(state).fields;
    return id === undefined ? errors : get(errors, id);
};
export const getCustomFieldOptionsErrors = (state, id) => {
    const errors = getCustomFieldErrors(state, id) || [];
    return id === undefined ? errors : get(errors, 'options');
};

export const getConditionalFieldErrorsByParentId = (state, id) => {
    const conditionalFields = getConditionalFieldsById(state, id);
    const errors = conditionalFields.reduce((acc, field) => {
        const fieldErrors = getCustomFieldErrors(state, field.id);
        if (fieldErrors) {
            acc[field.id] = fieldErrors;
        }
        return acc;
    }, {});
    return errors;
};

export const isCustomField = (state, id) => {
    if (id === undefined) {
        return true;
    }
    const { name = '' } = getFieldById(state, id);
    return name.indexOf('o-') === 0 && !DSAR_STANDARD_FIELDS_I18N[name];
};
export const isEditableField = (state, id) => {
    if (id === undefined) {
        return true;
    }
    const editable =
        isCustomField(state, id) || get(DSAR_STANDARD_FIELDS_I18N[id], 'editable') || false;
    return editable;
};
export const isRequiredField = (state, id) => {
    if (id === undefined) {
        return false;
    }
    const required = get(DSAR_STANDARD_FIELDS_I18N[id], 'required') || false;
    return required;
};
export const canAddConditionals = (state, id) => {
    const addConditionals = false !== get(DSAR_STANDARD_FIELDS_I18N[id], 'canAddConditionals');
    return addConditionals;
};
export const canShowHide = (state, id) => {
    const canShowHide = false !== get(DSAR_STANDARD_FIELDS_I18N[id], 'canShowHide');
    return canShowHide;
};
export const isExcludableField = (state, id) =>
    (!isCustomField(state, id) && get(DSAR_STANDARD_FIELDS_I18N[id], 'excludable')) || false;
export const isDraggable = (state, id) => {
    return isEditableField(state, id) || false;
};
export const isMandatoryField = (state, id) =>
    !isCustomField(state, id) && !isEditableField(state, id);
export const isLoading = state => getStore(state).loading;
export const isUpdating = state => getStore(state).updating;
export const isDisabledByDefault = (state, id) => {
    if (id === undefined) {
        return true;
    }

    const disabledByDefault = !!(
        DSAR_STANDARD_FIELDS_I18N[id]?.displayOption === DSAR_DISPLAY_OPTIONS.DISABLED
    );

    return disabledByDefault;
};

export const shouldShowConditionals = (state, id) => {
    const { type } = getFieldById(state, id);
    return type === DSAR_FIELD_TYPES.CHECKBOX;
};

export const shouldShowOptions = (state, id) => {
    switch (id) {
        case 'requestor-type':
        case 'request-type':
        case 'country':
        case 'territory': {
            return true;
        }
    }
    const { type } = getFieldById(state, id);
    return (
        type === DSAR_FIELD_TYPES.SELECT ||
        type === DSAR_FIELD_TYPES.MULTISELECT ||
        type === DSAR_FIELD_TYPES.RADIO
    );
};

export const getPayload = state => ({
    duplicateReject: getField(state, 'duplicateReject'),
    unverifiedEmailReject: getField(state, 'unverifiedEmailReject'),
    captchaEnabled: getField(state, 'captchaEnabled'),
    aiEmailParsing: getField(state, 'aiEmailParsing'),
    internalDueDays: getField(state, 'internalDueDays'),
    internalDueItems: getField(state, 'internalDueItems'),
    name: getField(state, 'name'),
    fromCompany: getField(state, 'fromCompany'),
    description: getField(state, 'description'),
    fromName: getField(state, 'fromName'),
    orgIds: getField(state, 'organizations')
        .map(({ orgId } = {}) => orgId)
        .filter(orgId => !!orgId && !getOrganizationById(state, orgId).isRoot),
    styling: {
        showHeader: !!getField(state, 'styling.showHeader'),
        instructions: getField(state, 'styling.instructions'),
        stylesheetURL: getField(state, 'styling.stylesheetURL'),
        palette: getField(state, 'styling.palette'),
    },
    dataStoreCount: getField(state, 'dataStoreCount'),
    allowListEmails: getField(state, 'allowListEmails'),
    escalationEmails: getField(state, 'escalationEmails'),
    ssoConfigId: (() => {
        const consumerSsoEnabled = getField(state, 'consumerSsoEnabled');
        if (!consumerSsoEnabled) {
            return null;
        }
        const consumerSsoId = getField(state, 'consumerSsoId');
        // Extract the value from the autocomplete object {label, value}
        return consumerSsoId?.value ?? consumerSsoId ?? null;
    })(),
    ssoRequired: (() => {
        const consumerSsoEnabled = getField(state, 'consumerSsoEnabled');
        if (!consumerSsoEnabled) {
            // When SSO is disabled, sso_required must be false to satisfy database constraint:
            // (sso_required = false) OR (sso_required = true AND sso_config_id IS NOT NULL)
            return false;
        }
        return getField(state, 'consumerSsoRequired') || false;
    })(),
    customFields: getFields(state).map(field => {
        const { id } = field;
        return Object.entries(field).reduce((newField, [key, value]) => {
            if (filteredKeys.includes(key)) {
                return newField;
            }
            if (mandatoryKeys.includes(key)) {
                newField[key] = value;
                return newField;
            }
            if (!isCustomField(state, id)) {
                // Parse the default fields, and only save things we can edit
                switch (key) {
                    case 'excluded': {
                        if (isExcludableField(state, id)) {
                            // If it's excludable, lets save the excluded key
                            newField[key] = !!value;
                            return newField;
                        }
                        break;
                    }

                    case 'accept': {
                        // filter null values from groups
                        newField[key] = castArray(value).filter(val => !!val);
                        return newField;
                    }

                    case 'helperText':
                    case 'label':
                    case 'placeholder': {
                        newField[key] = translate(value);
                        break;
                    }
                    case 'conditional':
                    case 'defaultValue':
                    case 'options': {
                        newField[key] = value;
                        break;
                    }
                }
                return newField;
            }
            if (value !== undefined && value !== '') {
                // filter null values from groups
                if (isArray(value)) {
                    newField[key] = value.filter(val => !!val);
                } else {
                    newField[key] = value;
                }
            }
            return newField;
        }, {});
    }),
});

export const isCountryHidden = (state, country) =>
    getFieldById(state, 'country').options.find(({ value, hidden }) => {
        return value === country && hidden;
    }) || false;
